import { Flex, Box, type BoxProps, Grid, Text } from '@shared/components';

function Cell(props: Readonly<BoxProps>) {
  const { children, sx } = props;
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: (theme) => theme.shape.rounded(1),
        minHeight: '40px',
        width: '100px',
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

interface ItemAnalysisDifficultyMatrixProps {
  title: string;
  easyPUsed?: {
    hard?: number;
    medium?: number;
    easy?: number;
  };
  mediumPUsed?: {
    hard?: number;
    medium?: number;
    easy?: number;
  };
  hardPUsed?: {
    hard?: number;
    medium?: number;
    easy?: number;
  };
  totalItems?: number | string;
}

interface CellColor {
  bgcolor: string;
  color: string;
}

export default function ItemAnalysisDifficultyMatrix(
  props: Readonly<ItemAnalysisDifficultyMatrixProps>,
) {
  const { title, easyPUsed, mediumPUsed, hardPUsed, totalItems = '-' } = props;

  const data = [
    {
      row: 'ง่าย',
      values: [easyPUsed?.easy ?? '-', easyPUsed?.medium ?? '-', easyPUsed?.hard ?? '-'],
    },
    {
      row: 'กลาง',
      values: [mediumPUsed?.easy ?? '-', mediumPUsed?.medium ?? '-', mediumPUsed?.hard ?? '-'],
    },
    {
      row: 'ยาก',
      values: [hardPUsed?.easy ?? '-', hardPUsed?.medium ?? '-', hardPUsed?.hard ?? '-'],
    },
  ];

  const COLOR = {
    success: { bgcolor: 'success.light', color: 'success.dark' },
    warning: { bgcolor: 'warning.light', color: 'warning.darker' },
    error: { bgcolor: 'error.light', color: 'error.dark' },
  } as const;

  const COLOR_MATRIX: Record<string, CellColor[]> = {
    ยาก: [COLOR.error, COLOR.warning, COLOR.success],
    กลาง: [COLOR.warning, COLOR.success, COLOR.warning],
    ง่าย: [COLOR.success, COLOR.warning, COLOR.error],
  };

  const getColor = (rowTitle: string, colIndex: number) =>
    COLOR_MATRIX[rowTitle]?.[colIndex] ?? COLOR.error;

  return (
    <Flex sx={{ width: '412px', gap: (theme) => theme.spacing(3) }}>
      <Text
        variant='body1Semibold'
        sx={{
          writingMode: 'vertical-rl',
          transform: 'rotate(180deg)',
          alignSelf: 'center',
        }}
      >
        P Used
      </Text>
      <Grid
        sx={{
          display: 'grid',
          gridTemplateColumns: '100px repeat(3, max(100px))',
          gridTemplateRows: 'auto repeat(4, 40px)',
          gap: (theme) => theme.spacing(1),
          width: '308px',
        }}
      >
        <Cell
          sx={{
            gridRow: '1 / span 2',
            bgcolor: (theme) => theme.palette.grey[700],
            color: (theme) => theme.palette.common.white,
          }}
        >
          <Text variant='body1Semibold'>{totalItems} ข้อ</Text>
        </Cell>
        <Cell
          sx={{
            gridColumn: '2 / span 4',
            bgcolor: (theme) => theme.palette.grey[700],
            color: (theme) => theme.palette.common.white,
            width: '308px',
          }}
        >
          <Text variant='body1Semibold'>{title}</Text>
        </Cell>

        {['ง่าย', 'กลาง', 'ยาก'].map((label, i) => (
          <Cell
            key={label}
            sx={{
              gridColumn: i + 2,
              bgcolor: (theme) => theme.palette.grey[100],
            }}
          >
            <Text variant='body1Semibold'>{label}</Text>
          </Cell>
        ))}

        {data.map((row, rowIndex) => (
          <>
            <Cell
              key={row.row}
              sx={{
                gridRow: rowIndex + 3,
                bgcolor: (theme) => theme.palette.grey[100],
              }}
            >
              <Text variant='body1Semibold'>{row.row}</Text>
            </Cell>

            {row.values.map((value, colIndex) => {
              const color = getColor(row.row, colIndex);

              return (
                <Cell
                  key={`${rowIndex}-${colIndex}`}
                  sx={{
                    gridRow: rowIndex + 3,
                    gridColumn: colIndex + 2,
                    ...color,
                  }}
                >
                  <Text variant='body1Semibold'>{value}</Text>
                </Cell>
              );
            })}
          </>
        ))}
      </Grid>
    </Flex>
  );
}
